import { type FormEvent, useEffect, useRef, useState } from "react";
import { demoPortal, portalRequest, uploadPortalDocument } from "./supplier-portal-gateway";
import { parsePortalWorkspace, parsePortalCommunications } from "./supplier-portal-contract";
import type { Costs, Workspace, PortalCommunications } from "./supplier-portal-model";

export function useSupplierPortalWorkspace() {
  const [workspace, setWorkspace] = useState<Workspace | null>(null),
    [communications, setCommunications] = useState<PortalCommunications>({
      questions: [],
      announcements: [],
    }),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false),
    [success, setSuccess] = useState("");
  const [currency, setCurrency] = useState("TRY"),
    [delivery, setDelivery] = useState(0),
    [payment, setPayment] = useState(0),
    [validityDate, setValidityDate] = useState(""),
    [note, setNote] = useState(""),
    [prices, setPrices] = useState<Record<string, string>>({}),
    [costs, setCosts] = useState<Costs>({
      shipping: "0",
      insurance: "0",
      installation: "0",
      other: "0",
    }),
    [declineReason, setDeclineReason] = useState(""),
    [questionBody, setQuestionBody] = useState("");

  const mounted = useRef(false), epoch = useRef(0), pending = useRef(false);
  const exchange = useRef<Promise<void> | null>(null);
  const load = async (isCurrent: () => boolean) => {
    const [data, communicationData] = await Promise.all([
      portalRequest("/portal/api/rfqs/current").then(parsePortalWorkspace),
      portalRequest("/portal/api/questions").then(parsePortalCommunications),
    ]);
    if (!isCurrent()) return;
    setWorkspace(data);
    setCommunications(communicationData);
    if (data.draft) {
      setCurrency(data.draft.currency); setDelivery(data.draft.deliveryLeadDays);
      setPayment(data.draft.paymentTermDays); setValidityDate(data.draft.validityDate);
      setCosts(data.draft.additionalCosts); setNote(data.draft.note ?? "");
      setPrices(Object.fromEntries(data.draft.items.map(i => [i.rfqItemId, String(i.unitPrice)])));
    }
  };
  useEffect(() => {
    mounted.current = true;
    const generation = ++epoch.current;
    const isCurrent = () => mounted.current && epoch.current === generation;
    pending.current = false; setBusy(false);
    if (!exchange.current) exchange.current = (async () => {
      const token = new URLSearchParams(location.search).get("token");
      if (token) {
        await portalRequest("/portal/api/access/exchange", {method:"POST",body:JSON.stringify({token})});
        history.replaceState({}, "", demoPortal ? `${location.pathname}?demo=1` : location.pathname);
      }
    })();
    void exchange.current.then(() => { if (isCurrent()) return load(isCurrent); }).catch(e => {
      if (isCurrent()) setError(e instanceof Error ? e.message : "Güvenli erişim kurulamadı.");
    });
    return () => { mounted.current = false; epoch.current += 1; };
  }, []);
  const payload = () => ({
    expectedVersion:workspace?.draft?.version,currency,deliveryLeadDays:delivery,paymentTermDays:payment,validityDate,
    shippingCost:costs.shipping,insuranceCost:costs.insurance,installationCost:costs.installation,otherCost:costs.other,note,
    items:workspace?.rfq.items.map(i=>({rfqItemId:i.id,unitPrice:prices[i.id]})),
  });
  const run = async (work: (isCurrent: () => boolean) => Promise<void>) => {
    if (!mounted.current || pending.current) return;
    const generation = epoch.current;
    const isCurrent = () => mounted.current && epoch.current === generation;
    pending.current = true; setBusy(true); setError(""); setSuccess("");
    try { await work(isCurrent); }
    catch (e) { if (isCurrent()) setError(e instanceof Error ? e.message : "İşlem tamamlanamadı."); }
    finally { if (isCurrent()) { pending.current = false; setBusy(false); } }
  };
  const persistDraft = async (isCurrent: () => boolean) => {
    await portalRequest("/portal/api/offers/draft", {method:"PUT",headers:{"idempotency-key":crypto.randomUUID()},body:JSON.stringify(payload())});
    if (isCurrent()) await load(isCurrent);
  };
  const save = async (event?: FormEvent) => {
    event?.preventDefault();
    await run(async isCurrent => { await persistDraft(isCurrent); if (isCurrent()) setSuccess("Taslak güvenle kaydedildi."); });
  };
  const submit = () => run(async isCurrent => {
    if (!workspace?.draft) await persistDraft(isCurrent);
    if (!isCurrent()) return;
    const current = parsePortalWorkspace(await portalRequest("/portal/api/rfqs/current"));
    if (!isCurrent()) return;
    if (!current.draft) throw new Error("Gönderilecek teklif taslağı bulunamadı.");
    await portalRequest("/portal/api/offers/submit", {method:"POST",headers:{"idempotency-key":crypto.randomUUID()},body:JSON.stringify({expectedVersion:current.draft.version})});
    if (!isCurrent()) return;
    await load(isCurrent);
    if (isCurrent()) setSuccess("Teklifiniz ANL'ye güvenle gönderildi.");
  });
  const decline = () => run(async isCurrent => {
    await portalRequest("/portal/api/offers/decline", {method:"POST",headers:{"idempotency-key":crypto.randomUUID()},body:JSON.stringify({reason:declineReason})});
    if (!isCurrent()) return;
    await load(isCurrent);
    if (isCurrent()) setSuccess("Katılmama bildiriminiz kaydedildi.");
  });
  const upload = (file: File) => run(async isCurrent => {
    if (!workspace?.draft) await persistDraft(isCurrent);
    if (!isCurrent()) return;
    await uploadPortalDocument(file);
    if (!isCurrent()) return;
    await load(isCurrent);
    if (isCurrent()) setSuccess("Belge güvenli tarama kuyruğuna alındı.");
  });
  const askQuestion = () => run(async isCurrent => {
    await portalRequest("/portal/api/questions", {method:"POST",headers:{"idempotency-key":crypto.randomUUID()},body:JSON.stringify({body:questionBody})});
    if (!isCurrent()) return;
    setQuestionBody(""); await load(isCurrent);
    if (isCurrent()) setSuccess("Sorunuz Satın Alma ekibine iletildi.");
  });
  return { workspace, communications, error, busy, success, currency, setCurrency, delivery, setDelivery, payment, setPayment, validityDate, setValidityDate, note, setNote, prices, setPrices, costs, setCosts, declineReason, setDeclineReason, questionBody, setQuestionBody, save, submit, decline, upload, askQuestion };
}
